import React from "react";

import { faBuilding, faFileAlt, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import styles from "../../noteTemplate/css/category.module.css";

export enum FixedCategory {
	ALL = "All templates",
	MY = "My templates",
	WORKSPACE = "Workspace templates",
	MEETING = "About a meeting",
	SOMEONE = "About a person",
	SOMETHING_ELSE = "About something else",
	UNSOLICITED = "Giving Feedback",
}

export const CategoryToPath: { [key: string]: string } = {
	[FixedCategory.ALL]: "all",
	[FixedCategory.MY]: "my",
	[FixedCategory.WORKSPACE]: "starred",
	[FixedCategory.MEETING]: "meeting",
	[FixedCategory.SOMEONE]: "someone",
	[FixedCategory.SOMETHING_ELSE]: "something-else",
	[FixedCategory.UNSOLICITED]: "unsolicited",
};

const CategoryIcon: { [key: string]: IconDefinition } = {
	[FixedCategory.ALL]: faFileAlt,
	[FixedCategory.MY]: faUser,
	[FixedCategory.WORKSPACE]: faBuilding,
};

export const PathToCategory: { [key: string]: FixedCategory } = {
	[CategoryToPath[FixedCategory.ALL]]: FixedCategory.ALL,
	[CategoryToPath[FixedCategory.MY]]: FixedCategory.MY,
	[CategoryToPath[FixedCategory.WORKSPACE]]: FixedCategory.WORKSPACE,
	[CategoryToPath[FixedCategory.MEETING]]: FixedCategory.MEETING,
	[CategoryToPath[FixedCategory.SOMEONE]]: FixedCategory.SOMEONE,
	[CategoryToPath[FixedCategory.SOMETHING_ELSE]]: FixedCategory.SOMETHING_ELSE,
	[CategoryToPath[FixedCategory.UNSOLICITED]]: FixedCategory.UNSOLICITED,
};

export type Category = FixedCategory;

function CategoryItem({
	category,
	onSelect,
	isSelected,
}: {
	category: Category;
	onSelect: (selection: Category) => void;
	isSelected?: boolean;
}) {
	const onClick = React.useCallback(() => {
		onSelect(category);
	}, [onSelect, category]);

	const cat = typeof category === "string" ? category : FixedCategory[category];
	const icon = CategoryIcon[cat.toString()];

	return (
		<button
			title={cat.toString()}
			onClick={onClick}
			className={cx(styles.categoryItem, { [styles.categoryItemActive]: isSelected })}
		>
			{icon && <FontAwesomeIcon fixedWidth icon={icon} />}
			{cat}
		</button>
	);
}

function CategorySection({
	categories,
	onSelect,
	selectedCategory,
}: {
	categories: readonly Category[];
	onSelect: (selection: Category) => void;
	selectedCategory?: Category;
}) {
	return (
		<section className={styles.categorySection}>
			{categories.map(c => (
				<CategoryItem key={c} category={c} isSelected={c === selectedCategory} onSelect={onSelect} />
			))}
		</section>
	);
}

const visibilityCategories = [FixedCategory.ALL, FixedCategory.MY, FixedCategory.WORKSPACE];
const typeCategories = [
	FixedCategory.MEETING,
	FixedCategory.SOMEONE,
	FixedCategory.SOMETHING_ELSE,
	FixedCategory.UNSOLICITED,
];

export const CategoryList: React.FC<{
	selectedCategory?: Category;
	onSelectionChange: (category: Category) => void;
}> = ({ selectedCategory, onSelectionChange }) => {
	return (
		<div className={styles.categoryList}>
			<CategorySection
				onSelect={onSelectionChange}
				selectedCategory={selectedCategory}
				categories={visibilityCategories}
			/>
			<CategorySection onSelect={onSelectionChange} selectedCategory={selectedCategory} categories={typeCategories} />
		</div>
	);
};
